import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const { login } = useAuth();
    const { t } = useT();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const from = location.state?.from || "/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const user = await login(email, password);
            toast.success(t("toast.welcomeBack", user.name?.split(" ")[0] || "guest"));
            navigate(["admin", "employee"].includes(user.role) ? "/admin" : from, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.signInFailed"));
        } finally {
            setSubmitting(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div data-testid="login-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 pb-24 px-6 lg:px-12">
                <div className="max-w-md mx-auto">
                    <div className="eyebrow mb-4 text-center">{t("auth.welcomeBack")}</div>
                    <h1 className="font-chiller text-5xl text-center leading-tight mb-12">{t("auth.signInTitle")}</h1>

                    <form onSubmit={submit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="eyebrow block mb-2">{t("auth.email")}</label>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3"
                            />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">{t("auth.password")}</label>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="login-submit"
                            disabled={submitting}
                            className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50"
                        >
                            {submitting ? t("auth.signingIn") : t("auth.signIn")}
                        </button>
                    </form>

                    <div className="my-8 flex items-center gap-4 text-xs uppercase tracking-widest2 text-espresso/60">
                        <div className="hairline flex-1" /> {t("auth.or")} <div className="hairline flex-1" />
                    </div>

                    <button
                        onClick={googleSignIn}
                        data-testid="login-google"
                        className="w-full px-6 py-4 border border-espresso/30 hover:bg-bone2 text-xs uppercase tracking-widest2 font-semibold inline-flex items-center justify-center gap-3"
                    >
                        <span className="w-4 h-4 rounded-full bg-gradient-to-br from-terracotta to-forest" /> {t("auth.continueGoogle")}
                    </button>

                    <p className="mt-10 text-center text-sm text-muted2">
                        {t("auth.newToRetreat")}{" "}
                        <Link to="/register" className="text-forest hover:text-terracotta underline" data-testid="link-to-register">
                            {t("auth.createOne")}
                        </Link>
                    </p>
                </div>
            </section>
            <Footer />
        </div>
    );
}
